import { Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AttentionConfig {
  has_training?: boolean;
  digits?: number[];
  target_digit?: number;
  training?: {
    items_per_digit?: number | null;
    custom?: number[] | null;
    show_summary?: boolean;
    finish_title?: string;
    finish_text?: string;
  };
  real_test?: {
    items_per_digit?: number | null;
    custom?: number[] | null;
    show_summary?: boolean;
  };
  digit_display_ms?: number;
  mask_display_ms?: number;
  correct_feedback_ms?: number;
  error_feedback_ms?: number;
  error_feedback_text?: string;
  omission_feedback_ms?: number;
  omission_feedback_text?: string;
  digit_font_sizes_rem?: number[];
  introduction?: {
    title?: string;
    instructions?: string[];
  };
}

interface ResolvedBlockConfig {
  items_per_digit: number | null;
  custom: number[] | null;
  show_summary: boolean;
}

interface ResolvedAttentionConfig {
  has_training: boolean;
  digits: number[];
  target_digit: number;
  training: ResolvedBlockConfig;
  real_test: ResolvedBlockConfig;
  digit_display_ms: number;
  mask_display_ms: number;
  correct_feedback_ms: number;
  error_feedback_ms: number;
  error_feedback_text: string;
  omission_feedback_ms: number;
  omission_feedback_text: string;
  digit_font_sizes_rem: number[];
}

export interface AttentionTaskAnswer {
  errors_count: number;
  omission_count: number;
  correct_count: number;
  displayed_sequence: number[];
  user_answers: Array<boolean | null>;
  time: number[];
}

interface Props {
  onAnswerChange: (taskAnswer?: AttentionTaskAnswer) => void;
  attentionConfig?: AttentionConfig;
  startButtonLabel?: string;
}

type AttentionPhase =
  | "intro"
  | "digit"
  | "mask"
  | "feedback"
  | "practice_break"
  | "done";
type FeedbackOutcome = "correct" | "error" | "omission";
type AttentionBlock = "practice" | "test";

interface Counters {
  errors_count: number;
  omission_count: number;
  correct_count: number;
}

interface PracticeSummary {
  totalRounds: number;
  roundsWithTarget: number;
  roundsWithoutTarget: number;
  errorsCount: number;
  omissionsCount: number;
  errorPercentage: number;
  omissionPercentage: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const INTRO_COUNTDOWN_SECONDS = 3;
const MASK_FONT_SIZE = "6.4rem";
const PRESS_ICON_SRC = `${process.env.PUBLIC_URL}/svg/press-icon.svg`;

const DEFAULT_CONFIG: ResolvedAttentionConfig = {
  has_training: true,
  digits: [1, 2, 3, 4, 5, 6, 7, 8, 9],
  target_digit: 3,
  training: {
    items_per_digit: 2,
    custom: null,
    show_summary: false,
  },
  real_test: {
    items_per_digit: 10,
    custom: null,
    show_summary: false,
  },
  digit_display_ms: 250,
  mask_display_ms: 900,
  correct_feedback_ms: 900,
  error_feedback_ms: 2000,
  error_feedback_text:
    "ATENÇÃO: Resposta incorreta! Não aperte a barra de espaço quando o número for o alvo.",
  omission_feedback_ms: 2000,
  omission_feedback_text:
    "ATENÇÃO: Aperte a barra de espaço para qualquer número diferente do alvo!",
  digit_font_sizes_rem: [4, 5, 6, 7, 8],
};

const INITIAL_COUNTERS: Counters = {
  errors_count: 0,
  omission_count: 0,
  correct_count: 0,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toSafeInt = (
  value: unknown,
  fallback: number,
  min: number
): number => {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, n);
};

const shuffleArray = (
  sequence: number[],
  randomFn: () => number = Math.random
): number[] => {
  const result = [...sequence];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(randomFn() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

const buildBalancedSequence = (digits: number[], itemsPerDigit: number): number[] => {
  const sequence: number[] = [];
  digits.forEach((digit) => {
    for (let i = 0; i < itemsPerDigit; i += 1) {
      sequence.push(digit);
    }
  });
  return sequence;
};

const resolveBlockConfig = (
  raw: AttentionConfig["training"] | undefined,
  defaults: ResolvedBlockConfig
): ResolvedBlockConfig => {
  if (!raw) return { ...defaults };

  const custom =
    Array.isArray(raw.custom) && raw.custom.length > 0
      ? raw.custom.filter(Number.isInteger)
      : null;

  const items_per_digit =
    custom !== null
      ? null
      : toSafeInt(raw.items_per_digit ?? defaults.items_per_digit, defaults.items_per_digit ?? 1, 1);

  return {
    items_per_digit,
    custom,
    show_summary:
      typeof raw.show_summary === "boolean" ? raw.show_summary : defaults.show_summary,
  };
};

const resolveAttentionConfig = (raw?: AttentionConfig): ResolvedAttentionConfig => {
  if (!raw) return DEFAULT_CONFIG;

  const digits =
    Array.isArray(raw.digits) && raw.digits.length > 0
      ? raw.digits.filter(Number.isInteger)
      : DEFAULT_CONFIG.digits;

  const target_digit = Number.isInteger(raw.target_digit)
    ? (raw.target_digit as number)
    : DEFAULT_CONFIG.target_digit;

  const digit_font_sizes_rem =
    Array.isArray(raw.digit_font_sizes_rem) && raw.digit_font_sizes_rem.length > 0
      ? raw.digit_font_sizes_rem.map((v) => Math.max(1, Number(v) || 4))
      : DEFAULT_CONFIG.digit_font_sizes_rem;

  return {
    has_training:
      typeof raw.has_training === "boolean"
        ? raw.has_training
        : DEFAULT_CONFIG.has_training,
    digits,
    target_digit,
    training: resolveBlockConfig(raw.training, DEFAULT_CONFIG.training),
    real_test: resolveBlockConfig(raw.real_test, DEFAULT_CONFIG.real_test),
    digit_display_ms: toSafeInt(raw.digit_display_ms, DEFAULT_CONFIG.digit_display_ms, 100),
    mask_display_ms: toSafeInt(raw.mask_display_ms, DEFAULT_CONFIG.mask_display_ms, 0),
    correct_feedback_ms: toSafeInt(raw.correct_feedback_ms, DEFAULT_CONFIG.correct_feedback_ms, 0),
    error_feedback_ms: toSafeInt(raw.error_feedback_ms, DEFAULT_CONFIG.error_feedback_ms, 0),
    error_feedback_text:
      typeof raw.error_feedback_text === "string"
        ? raw.error_feedback_text
        : DEFAULT_CONFIG.error_feedback_text,
    omission_feedback_ms: toSafeInt(raw.omission_feedback_ms, DEFAULT_CONFIG.omission_feedback_ms, 0),
    omission_feedback_text:
      typeof raw.omission_feedback_text === "string"
        ? raw.omission_feedback_text
        : DEFAULT_CONFIG.omission_feedback_text,
    digit_font_sizes_rem,
  };
};

const buildSequence = (config: ResolvedAttentionConfig, block: AttentionBlock): number[] => {
  const blockConfig = block === "practice" ? config.training : config.real_test;

  if (blockConfig.custom !== null) {
    return [...blockConfig.custom];
  }

  const itemsPerDigit = blockConfig.items_per_digit ?? 1;
  return shuffleArray(buildBalancedSequence(config.digits, itemsPerDigit));
};

const getFeedbackDurationInMs = (
  outcome: FeedbackOutcome,
  config: ResolvedAttentionConfig
): number => {
  if (outcome === "correct") return config.correct_feedback_ms;
  if (outcome === "error") return config.error_feedback_ms;
  return config.omission_feedback_ms;
};

const getOutcome = (digit: number, didPress: boolean, targetDigit: number): FeedbackOutcome => {
  if (digit === targetDigit) return didPress ? "error" : "correct";
  return didPress ? "correct" : "omission";
};

const toUserAnswer = (digit: number, didPress: boolean, targetDigit: number): boolean | null => {
  if (digit === targetDigit) return didPress ? false : true;
  return didPress ? true : null;
};

const pickRandomFontSize = (fontSizesRem: number[]): string => {
  const sizes = fontSizesRem.length > 0 ? fontSizesRem : [6];
  return `${sizes[Math.floor(Math.random() * sizes.length)]}rem`;
};

const toPercentage = (value: number, total: number): number =>
  total <= 0 ? 0 : (value / total) * 100;

const formatPercentage = (value: number): string =>
  `${value.toFixed(1).replace(".", ",")}%`;

const buildSummary = (
  displayedSequence: number[],
  errorsCount: number,
  omissionsCount: number,
  targetDigit: number
): PracticeSummary => {
  const roundsWithTarget = displayedSequence.filter((d) => d === targetDigit).length;
  const roundsWithoutTarget = Math.max(0, displayedSequence.length - roundsWithTarget);

  return {
    totalRounds: displayedSequence.length,
    roundsWithTarget,
    roundsWithoutTarget,
    errorsCount,
    omissionsCount,
    errorPercentage: toPercentage(errorsCount, roundsWithTarget),
    omissionPercentage: toPercentage(omissionsCount, roundsWithoutTarget),
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

const DEFAULT_INTRODUCTION_TITLE = "Tarefa de atenção sustentada";
const DEFAULT_INTRODUCTION_INSTRUCTIONS = [
  "Nesta tarefa, números de 1 a 9 vão aparecer rapidamente no centro da tela.",
  "Você deve pressionar a barra de espaço para todos os números que aparecerem, EXCETO para o número 3.",
  "Por exemplo, quando o número 1 aparecer, pressione a barra de espaço.  Quando o número 7 aparecer, você também deve apertar a barra de espaço, e assim, sucessivamente. Porém, se o número 3 aparecer, não aperte nada!",
  "Seja o mais rápido que você puder."
];

const AttentionQuestion: React.FC<Props> = ({
  onAnswerChange,
  attentionConfig,
  startButtonLabel = "Iniciar treino",
}) => {
  const resolvedConfig = React.useMemo(
    () => resolveAttentionConfig(attentionConfig),
    [attentionConfig]
  );

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const timeoutIdsRef = React.useRef<number[]>([]);
  const sequenceRef = React.useRef<number[]>([]);
  const displayedSequenceRef = React.useRef<number[]>([]);
  const userAnswersRef = React.useRef<Array<boolean | null>>([]);
  const reactionTimesRef = React.useRef<number[]>([]);
  const countersRef = React.useRef<Counters>(INITIAL_COUNTERS);
  const didPressOnCurrentRoundRef = React.useRef<boolean>(false);
  const reactionTimeOnCurrentRoundRef = React.useRef<number>(0);
  const roundStartTimeRef = React.useRef<number>(0);
  const activeRoundRef = React.useRef<{
    roundIndex: number;
    digit: number;
    totalResponseWindowInMs: number;
  } | null>(null);
  const startRoundRef = React.useRef<(roundIndex: number) => void>(() => undefined);

  // ── State ────────────────────────────────────────────────────────────────────
  const [phase, setPhase] = React.useState<AttentionPhase>("intro");
  const [currentBlock, setCurrentBlock] = React.useState<AttentionBlock>("practice");
  const [currentDigit, setCurrentDigit] = React.useState<number | null>(null);
  const [currentDigitFontSize, setCurrentDigitFontSize] = React.useState<string>("6rem");
  const [feedbackOutcome, setFeedbackOutcome] = React.useState<FeedbackOutcome | null>(null);
  const [isStartingCountdown, setIsStartingCountdown] = React.useState<boolean>(false);
  const [countdownValue, setCountdownValue] = React.useState<number>(INTRO_COUNTDOWN_SECONDS);
  const [isRunning, setIsRunning] = React.useState<boolean>(false);
  const [practiceSummary, setPracticeSummary] = React.useState<PracticeSummary | null>(null);
  const [taskAnswer, setTaskAnswer] = React.useState<AttentionTaskAnswer | undefined>(undefined);

  // ── Callbacks ─────────────────────────────────────────────────────────────────
  const clearTimers = React.useCallback(() => {
    timeoutIdsRef.current.forEach((id: number) => window.clearTimeout(id));
    timeoutIdsRef.current = [];
  }, []);

  const resetRoundRefs = React.useCallback(() => {
    displayedSequenceRef.current = [];
    userAnswersRef.current = [];
    reactionTimesRef.current = [];
    countersRef.current = { ...INITIAL_COUNTERS };
    didPressOnCurrentRoundRef.current = false;
    reactionTimeOnCurrentRoundRef.current = 0;
    roundStartTimeRef.current = 0;
    activeRoundRef.current = null;
  }, []);

  const resetTaskState = React.useCallback(() => {
    resetRoundRefs();

    setCurrentDigit(null);
    setCurrentDigitFontSize("6rem");
    setFeedbackOutcome(null);
    setIsStartingCountdown(false);
    setCountdownValue(INTRO_COUNTDOWN_SECONDS);
    setCurrentBlock("practice");
    setIsRunning(false);
    setPracticeSummary(null);
    setTaskAnswer(undefined);
  }, [resetRoundRefs]);

  const finishTask = React.useCallback(() => {
    clearTimers();
    const finalAnswer: AttentionTaskAnswer = {
      ...countersRef.current,
      displayed_sequence: [...displayedSequenceRef.current],
      user_answers: [...userAnswersRef.current],
      time: [...reactionTimesRef.current],
    };

    setIsRunning(false);
    setCurrentDigit(null);
    setFeedbackOutcome(null);
    setPhase("done");
    setTaskAnswer(finalAnswer);
    onAnswerChange(finalAnswer);
  }, [clearTimers, onAnswerChange]);

  const completeRound = React.useCallback(
    (
      roundIndex: number,
      digit: number,
      totalResponseWindowInMs: number,
      outcome: FeedbackOutcome
    ) => {
      const didPress = didPressOnCurrentRoundRef.current;
      const reactionTime = didPress
        ? reactionTimeOnCurrentRoundRef.current
        : totalResponseWindowInMs;

      displayedSequenceRef.current = [...displayedSequenceRef.current, digit];
      userAnswersRef.current = [
        ...userAnswersRef.current,
        toUserAnswer(digit, didPress, resolvedConfig.target_digit),
      ];
      reactionTimesRef.current = [...reactionTimesRef.current, reactionTime];

      const nextCounters: Counters = {
        errors_count: countersRef.current.errors_count + (outcome === "error" ? 1 : 0),
        omission_count: countersRef.current.omission_count + (outcome === "omission" ? 1 : 0),
        correct_count: countersRef.current.correct_count + (outcome === "correct" ? 1 : 0),
      };
      countersRef.current = nextCounters;
      activeRoundRef.current = null;

      setFeedbackOutcome(null);
      const nextRoundIndex = roundIndex + 1;

      if (nextRoundIndex >= sequenceRef.current.length) {
        if (currentBlock === "practice") {
          setPracticeSummary(
            buildSummary(
              [...displayedSequenceRef.current],
              nextCounters.errors_count,
              nextCounters.omission_count,
              resolvedConfig.target_digit
            )
          );
          clearTimers();
          setIsRunning(false);
          setCurrentDigit(null);
          setFeedbackOutcome(null);
          setPhase("practice_break");
          return;
        }

        finishTask();
        return;
      }

      startRoundRef.current(nextRoundIndex);
    },
    [clearTimers, currentBlock, finishTask, resolvedConfig.target_digit]
  );

  const startFeedbackPhase = React.useCallback(
    (
      roundIndex: number,
      digit: number,
      totalResponseWindowInMs: number,
      outcome: FeedbackOutcome
    ) => {
      setFeedbackOutcome(outcome);
      setPhase("feedback");

      const durationMs = getFeedbackDurationInMs(outcome, resolvedConfig);
      const id = window.setTimeout(() => {
        completeRound(roundIndex, digit, totalResponseWindowInMs, outcome);
      }, durationMs);
      timeoutIdsRef.current.push(id);
    },
    [completeRound, resolvedConfig]
  );

  const resolveRoundAfterMask = React.useCallback(
    (roundIndex: number, digit: number, totalResponseWindowInMs: number) => {
      const didPress = didPressOnCurrentRoundRef.current;
      const outcome = getOutcome(digit, didPress, resolvedConfig.target_digit);

      // Correct non-press on target has no feedback — move immediately
      if (digit === resolvedConfig.target_digit && !didPress) {
        completeRound(roundIndex, digit, totalResponseWindowInMs, outcome);
        return;
      }

      startFeedbackPhase(roundIndex, digit, totalResponseWindowInMs, outcome);
    },
    [completeRound, resolvedConfig.target_digit, startFeedbackPhase]
  );

  const startRound = React.useCallback(
    (roundIndex: number) => {
      clearTimers();
      const currentSequence = sequenceRef.current;
      if (roundIndex >= currentSequence.length) {
        finishTask();
        return;
      }

      const digit = currentSequence[roundIndex];
      const digitVisibleMs = resolvedConfig.digit_display_ms;
      const totalResponseWindowInMs = digitVisibleMs + resolvedConfig.mask_display_ms;

      didPressOnCurrentRoundRef.current = false;
      reactionTimeOnCurrentRoundRef.current = totalResponseWindowInMs;
      roundStartTimeRef.current = performance.now();
      activeRoundRef.current = { roundIndex, digit, totalResponseWindowInMs };

      setCurrentDigit(digit);
      setCurrentDigitFontSize(pickRandomFontSize(resolvedConfig.digit_font_sizes_rem));
      setFeedbackOutcome(null);
      setPhase("digit");

      timeoutIdsRef.current.push(
        window.setTimeout(() => setPhase("mask"), digitVisibleMs)
      );
      timeoutIdsRef.current.push(
        window.setTimeout(
          () => resolveRoundAfterMask(roundIndex, digit, totalResponseWindowInMs),
          totalResponseWindowInMs
        )
      );
    },
    [
      clearTimers,
      finishTask,
      resolveRoundAfterMask,
      resolvedConfig.digit_display_ms,
      resolvedConfig.mask_display_ms,
      resolvedConfig.digit_font_sizes_rem,
    ]
  );

  React.useEffect(() => {
    startRoundRef.current = startRound;
  }, [startRound]);

  const startBlock = React.useCallback(
    (block: AttentionBlock) => {
      clearTimers();
      resetRoundRefs();

      setCurrentDigit(null);
      setCurrentDigitFontSize("6rem");
      setFeedbackOutcome(null);
      setIsStartingCountdown(false);
      setCountdownValue(INTRO_COUNTDOWN_SECONDS);
      setTaskAnswer(undefined);
      if (block === "practice") {
        setPracticeSummary(null);
      }
      setCurrentBlock(block);
      onAnswerChange(undefined);

      const nextSequence = buildSequence(resolvedConfig, block);
      sequenceRef.current = nextSequence;

      setIsRunning(true);
      setPhase("digit");

      if (nextSequence.length === 0) {
        finishTask();
        return;
      }

      startRoundRef.current(0);
    },
    [clearTimers, resetRoundRefs, finishTask, onAnswerChange, resolvedConfig]
  );

  const startPractice = React.useCallback(() => startBlock("practice"), [startBlock]);
  const startTest = React.useCallback(() => startBlock("test"), [startBlock]);

  const startCountdown = React.useCallback(() => {
    if (isStartingCountdown) return;
    setIsStartingCountdown(true);
    setCountdownValue(INTRO_COUNTDOWN_SECONDS);
  }, [isStartingCountdown]);

  // ── Effects ───────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    onAnswerChange(undefined);
    resetTaskState();
    setPhase("intro");

    return () => {
      clearTimers();
    };
  }, [clearTimers, onAnswerChange, resetTaskState]);

  React.useEffect(() => {
    if (!isStartingCountdown || phase !== "intro") {
      return;
    }

    if (countdownValue <= 1) {
      const id = window.setTimeout(() => {
        setIsStartingCountdown(false);
        setCountdownValue(INTRO_COUNTDOWN_SECONDS);
        if (resolvedConfig.has_training) {
          startPractice();
        } else {
          startTest();
        }
      }, 1000);

      return () => window.clearTimeout(id);
    }

    const id = window.setTimeout(() => {
      setCountdownValue((prev: number) => prev - 1);
    }, 1000);

    return () => window.clearTimeout(id);
  }, [
    countdownValue,
    isStartingCountdown,
    phase,
    resolvedConfig.has_training,
    startPractice,
    startTest,
  ]);

  // ── Keyboard handler ─────────────────────────────────────────────────────────
  const registerSpaceResponse = React.useCallback(() => {
    if (!isRunning || (phase !== "digit" && phase !== "mask")) {
      return;
    }

    if (didPressOnCurrentRoundRef.current) {
      return;
    }

    didPressOnCurrentRoundRef.current = true;
    reactionTimeOnCurrentRoundRef.current = Math.max(
      0,
      Math.round(performance.now() - roundStartTimeRef.current)
    );

    const activeRound = activeRoundRef.current;
    if (!activeRound) {
      return;
    }

    clearTimers();
    const outcome: FeedbackOutcome =
      activeRound.digit === resolvedConfig.target_digit ? "error" : "correct";

    startFeedbackPhase(
      activeRound.roundIndex,
      activeRound.digit,
      activeRound.totalResponseWindowInMs,
      outcome
    );
  }, [clearTimers, isRunning, phase, resolvedConfig.target_digit, startFeedbackPhase]);

  React.useEffect(() => {
    const onWindowKeyDown = (event: KeyboardEvent) => {
      const isSpace = event.code === "Space" || event.key === " ";
      if (!isSpace || event.repeat) return;
      event.preventDefault();
      registerSpaceResponse();
    };

    window.addEventListener("keydown", onWindowKeyDown);
    return () => window.removeEventListener("keydown", onWindowKeyDown);
  }, [registerSpaceResponse]);

  // ── Render helpers ───────────────────────────────────────────────────────────
  const renderStimulus = () => {
    if (phase === "digit") {
      return (
        <Typography
          variant="h1"
          sx={{ color: "#ffffff", fontSize: currentDigitFontSize }}
        >
          {currentDigit}
        </Typography>
      );
    }

    if (phase === "mask") {
      return (
        <Box
          component="img"
          src={PRESS_ICON_SRC}
          alt="Pressione"
          sx={{
            display: "block",
            width: MASK_FONT_SIZE,
            height: MASK_FONT_SIZE,
            objectFit: "contain",
            filter: "brightness(0) invert(1)",
          }}
        />
      );
    }

    if (phase === "feedback") {
      if (feedbackOutcome === "correct") {
        return (
          <CheckCircleOutlineIcon
            sx={{ color: "#43a047", fontSize: MASK_FONT_SIZE }}
          />
        );
      }

      if (feedbackOutcome === "error" || feedbackOutcome === "omission") {
        const feedbackText =
          feedbackOutcome === "error"
            ? resolvedConfig.error_feedback_text
            : resolvedConfig.omission_feedback_text;

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.25,
              maxWidth: 460,
              px: 2,
            }}
          >
            <HighlightOffIcon
              sx={{ color: "#ff1744", fontSize: { xs: "2.5rem", md: "4rem" } }}
            />
            <Typography
              variant="h5"
              sx={{ color: "#ffffff", textAlign: "center", fontWeight: 700 }}
            >
              {feedbackText}
            </Typography>
          </Box>
        );
      }
    }

    return (
      <Typography variant="body1" sx={{ color: "#ffffff", textAlign: "center" }}>
        Aguarde...
      </Typography>
    );
  };

  const isTaskFinished = phase === "done";
  const isPracticeBreak = phase === "practice_break";

  const finalSummary = React.useMemo<PracticeSummary | null>(() => {
    if (!taskAnswer) return null;
    return buildSummary(
      taskAnswer.displayed_sequence,
      taskAnswer.errors_count,
      taskAnswer.omission_count,
      resolvedConfig.target_digit
    );
  }, [taskAnswer, resolvedConfig.target_digit]);

  const practiceRoundCount =
    resolvedConfig.training.custom !== null
      ? resolvedConfig.training.custom.length
      : (resolvedConfig.training.items_per_digit ?? 1) * resolvedConfig.digits.length;

  return (
    <Box
      width="100%"
      marginTop={6}
      sx={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        maxWidth: 760,
      }}
    >
      {phase === "intro" ? (
        <Box
          sx={{
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: "grey.100",
            borderRadius: "8px",
            padding: { xs: "1rem", md: "1.25rem" },
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="primary" sx={{ marginBottom: "0.75rem" }}>
            {attentionConfig?.introduction?.title ?? DEFAULT_INTRODUCTION_TITLE}
          </Typography>
          <Box
            component="ul"
            sx={{
              textAlign: "left",
              listStyleType: "disc",
              paddingLeft: "1.25rem",
              margin: 0,
              marginBottom: "1rem",
            }}
          >
            {(attentionConfig?.introduction?.instructions ?? [
              ...DEFAULT_INTRODUCTION_INSTRUCTIONS,
              ...(resolvedConfig.has_training
                ? ["Primeiro você fará algumas rodadas de treino. Em seguida, haverá uma pausa curta para iniciar a tarefa real."]
                : []),
            ]).map((line: string, i: number) => (
              <Box component="li" key={i} sx={{ marginBottom: "0.4rem" }}>
                <Typography variant="body2" color="text.secondary">
                  {line}
                </Typography>
              </Box>
            ))}
          </Box>
          {isStartingCountdown ? (
            <Typography variant="h6" color="primary">
              {`Começando em ${countdownValue}...`}
            </Typography>
          ) : (
            <Button
              variant="contained"
              sx={{ borderRadius: 999, px: 3, py: 1, color: "white" }}
              onClick={startCountdown}
            >
              {startButtonLabel}
            </Button>
          )}
        </Box>
      ) : null}

      {isPracticeBreak ? (
        <Box
          sx={{
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: "grey.100",
            borderRadius: "8px",
            padding: { xs: "1rem", md: "1.25rem" },
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="primary" sx={{ marginBottom: "0.75rem" }}>
            {attentionConfig?.training?.finish_title ?? "Parabéns, você completou a fase de treino!"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ marginBottom: "1rem" }}
          >
            {attentionConfig?.training?.finish_text ?? `A tarefa irá continuar. Lembre-se de pressionar a barra de espaço para os números de 1 a 9, EXCETO para o número 3.`}
          </Typography>
          {resolvedConfig.training.show_summary && practiceSummary ? (
            <Box
              sx={{
                textAlign: "left",
                backgroundColor: "rgba(255,255,255,0.75)",
                borderRadius: "8px",
                padding: "0.75rem 1rem",
                marginBottom: "1rem",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Rodadas no treino: {practiceSummary.totalRounds}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rodadas com número {resolvedConfig.target_digit}:{" "}
                {practiceSummary.roundsWithTarget} | Erros: {practiceSummary.errorsCount}{" "}
                | Porcentagem de erro: {formatPercentage(practiceSummary.errorPercentage)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rodadas sem número {resolvedConfig.target_digit}:{" "}
                {practiceSummary.roundsWithoutTarget} | Omissões:{" "}
                {practiceSummary.omissionsCount} | Porcentagem de omissão:{" "}
                {formatPercentage(practiceSummary.omissionPercentage)}
              </Typography>
            </Box>
          ) : null}
          <Button
            variant="contained"
            sx={{ borderRadius: 999, px: 3, py: 1, color: "white" }}
            onClick={startTest}
          >
            Iniciar teste real
          </Button>
        </Box>
      ) : null}

      {phase !== "intro" && !isPracticeBreak ? (
        <Box
          sx={{
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: "grey.100",
            borderRadius: "8px",
            padding: { xs: "1rem", md: "1.25rem" },
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#000000",
              borderRadius: "10px",
              minHeight: { xs: 220, md: 280 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isTaskFinished ? (
              resolvedConfig.real_test.show_summary && finalSummary ? (
                <Box sx={{ width: "100%", maxWidth: 560, px: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: "#ffffff", textAlign: "center", mb: 1 }}
                  >
                    Resultados da tarefa
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#ffffff" }}>
                    Rodadas: {finalSummary.totalRounds}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#ffffff" }}>
                    Rodadas com número {resolvedConfig.target_digit}:{" "}
                    {finalSummary.roundsWithTarget} | Erros: {finalSummary.errorsCount} |
                    Porcentagem de erro:{" "}
                    {formatPercentage(finalSummary.errorPercentage)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#ffffff" }}>
                    Rodadas sem número {resolvedConfig.target_digit}:{" "}
                    {finalSummary.roundsWithoutTarget} | Omissões:{" "}
                    {finalSummary.omissionsCount} | Porcentagem de omissão:{" "}
                    {formatPercentage(finalSummary.omissionPercentage)}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="h6"
                  sx={{ color: "#ffffff", textAlign: "center" }}
                >
                  Tarefa concluída!
                </Typography>
              )
            ) : (
              renderStimulus()
            )}
          </Box>

          {!isTaskFinished ? (
            <Button
              variant="contained"
              onClick={registerSpaceResponse}
              disabled={!isRunning || (phase !== "digit" && phase !== "mask")}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                alignSelf: "center",
                borderRadius: 999,
                px: 3,
                py: 1,
                color: "white",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              barra de espaço
            </Button>
          ) : null}

          {isTaskFinished ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              Resultado pronto. Clique em "Próximo" para continuar.
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default AttentionQuestion;
